import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { ArrowLeft, ImageSquare } from "@phosphor-icons/react";
import TradingViewChart from "../components/TradingViewChart";

export default function TradeDetail() {
  const { id } = useParams();
  const { accounts } = useAccounts();
  const [trade, setTrade] = useState(null);
  const tradeAccount = trade ? accounts.find(a => a.id === trade.account_id) : null;

  const load = async () => {
    const { data } = await api.get(`/trades/${id}`);
    setTrade(data);
  };
  useEffect(() => { load(); }, [id]);

  const onDrop = async (files) => {
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/trades/${id}/charts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded ${f.name}`);
      } catch (e) {
        toast.error(`Failed: ${e?.response?.data?.detail || f.name}`);
      }
    }
    load();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] } });

  if (!trade) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <Link to="/trades" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Back to trades
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Trade Detail</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{trade.symbol}</h1>
          <p className={`font-mono-num text-2xl font-bold mt-2 ${trade.pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
            {moneyFor(tradeAccount, trade.pnl, { sign: true })}
          </p>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          <div>{trade.direction.toUpperCase()} · qty {trade.quantity}</div>
          <div>Entry {trade.entry_price} → Exit {trade.exit_price}</div>
          <div>{(trade.entry_time || "").slice(0,10)}</div>
          {(trade.tags || []).length > 0 && (
            <div className="mt-1 flex justify-end flex-wrap gap-1">
              {trade.tags.map(t => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* TradingView chart replay */}
      <div className="mb-6">
        <TradingViewChart symbol={trade.symbol} entryTime={trade.entry_time} />
      </div>

      {trade.notes && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Notes &amp; Trade Logic</p>
          <p className="text-sm whitespace-pre-wrap" data-testid="trade-detail-notes">{trade.notes}</p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">Chart Screenshots</p>

        <div {...getRootProps()} data-testid="chart-dropzone"
          className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors duration-200 ${isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
          <input {...getInputProps()} />
          <ImageSquare size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm">Drop chart screenshots here, or click to select</p>
          <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP</p>
        </div>

        {trade.charts?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            {trade.charts.map(c => (
              <a key={c.id} href={fileUrl(c.storage_path)} target="_blank" rel="noreferrer"
                className="block rounded-lg overflow-hidden border border-border hover:border-primary/50">
                <img src={fileUrl(c.storage_path)} alt="chart" className="w-full h-40 object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
